import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useApiEntity } from '../../hooks/use-api-entity'
import { useModal } from '../../hooks/use-modal'
import { api } from '../../services/api'
import {
  PageHeader,
  FormField,
  Alert,
  ConfirmDialog,
  btnPrimary,
  btnSecondary,
  btnDanger,
  card,
  input,
  colors,
} from '../../components/ui'
import { DisclosureViewer } from './DisclosureViewer.js'
import { useFeatures } from '../../hooks/use-features.js'

interface PatientForm {
  firstName: string
  lastName: string
  middleName: string
  dateOfBirth: string
  sex: string
  medicalRecordNumber: string
  externalId: string
  email: string
  phone: string
  street: string
  city: string
  state: string
  zip: string
  country: string
}

const defaults: PatientForm = {
  firstName: '',
  lastName: '',
  middleName: '',
  dateOfBirth: '',
  sex: 'unknown',
  medicalRecordNumber: '',
  externalId: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
}

export function PatientEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const features = useFeatures()
  const { data, isNew, loading, saving, error, success, save, cancel, update, setError } = useApiEntity<PatientForm>({
    path: '/patients',
    listPath: '/patients',
    defaultValue: defaults,
  })

  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  const handleArchive = async () => {
    setArchiving(true)
    try {
      await api.delete(`/patients/${id}`)
      navigate('/patients')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive patient')
      archiveModal.close()
    } finally {
      setArchiving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader
        title={isNew ? 'New Patient' : `${data.lastName}, ${data.firstName}`}
        actions={
          <>
            {!isNew && (
              <button onClick={archiveModal.open} style={btnDanger}>
                Archive
              </button>
            )}
            <button onClick={cancel} style={btnSecondary}>
              Cancel
            </button>
            <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      />
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <div style={{ ...card, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
        <FormField
          name="firstName"
          label="First Name"
          required
          value={data.firstName}
          onChange={(v) => update('firstName', v)}
        />
        <FormField
          name="lastName"
          label="Last Name"
          required
          value={data.lastName}
          onChange={(v) => update('lastName', v)}
        />
        <FormField
          name="middleName"
          label="Middle Name"
          value={data.middleName}
          onChange={(v) => update('middleName', v)}
        />
        <FormField
          name="dateOfBirth"
          label="Date of Birth"
          required
          type="date"
          value={data.dateOfBirth}
          onChange={(v) => update('dateOfBirth', v)}
        />
        <FormField name="sex" label="Sex" required>
          <select style={input} value={data.sex} onChange={(e) => update('sex', e.target.value)}>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="unknown">Unknown</option>
          </select>
        </FormField>
        <FormField
          name="medicalRecordNumber"
          label="MRN"
          value={data.medicalRecordNumber}
          onChange={(v) => update('medicalRecordNumber', v)}
        />
        <FormField
          name="externalId"
          label="External ID"
          value={data.externalId}
          onChange={(v) => update('externalId', v)}
        />
        <FormField name="email" label="Email" type="email" value={data.email} onChange={(v) => update('email', v)} />
        <FormField name="phone" label="Phone" value={data.phone} onChange={(v) => update('phone', v)} />

        <div
          style={{
            gridColumn: '1 / -1',
            borderTop: `1px solid ${colors.border}`,
            paddingTop: '1rem',
            marginTop: '0.5rem',
          }}
        >
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: colors.text }}>Address</h3>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <FormField name="street" label="Street" value={data.street} onChange={(v) => update('street', v)} />
        </div>
        <FormField name="city" label="City" value={data.city} onChange={(v) => update('city', v)} />
        <FormField name="state" label="State" value={data.state} onChange={(v) => update('state', v)} />
        <FormField name="zip" label="ZIP" value={data.zip} onChange={(v) => update('zip', v)} />
      </div>

      {!isNew && id && features.hipaa && <DisclosureViewer patientId={id} />}

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Patient"
        message={`Are you sure you want to archive ${data.firstName} ${data.lastName}? This patient will be excluded from search results.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  )
}
